(function () {
   'use strict';

   // ============================================================================
   // CUSTOM AOS IMPLEMENTATION
   // Replaces AOS library with custom IntersectionObserver-based solution
   // ============================================================================
   //
   // WHY THIS REPLACES AOS.JS:
   // -------------------------
   // 1. Performance: Uses native IntersectionObserver (no scroll event listeners)
   // 2. Automatic DOM Detection: MutationObserver auto-detects new [data-aos] elements
   // 3. Better Mobile Control: Dynamic disableBelow with resize debouncing
   // 4. Cleaner State Management: Proper observer lifecycle (enable/disable/destroy)
   // 5. Manual Control: data-aos-manual for lazy-loaded content
   // 6. No Flash of Content (FOC): Immediate disable class prevents flashing
   // 7. Smaller Footprint: Only includes animations you need
   //
   // USAGE:
   // ------
   // Initialize with options (usually done in aos-init.js):
   //   CustomAOS.init({
   //     offset: 120,
   //     once: true,
   //     disableBelow: 768,
   //     observeDomChanges: true
   //   });
   //
   // HTML attributes:
   //   <div data-aos="fade-up">Content</div>
   //   <div data-aos="zoom-in" data-aos-duration="800" data-aos-delay="200">Content</div>
   //   <div data-aos="fade" data-aos-offset="200" data-aos-once="false">Content</div>
   //   <div data-aos="slide-up" data-aos-manual>Lazy-loaded content</div>
   //   <div data-aos="fade-up" data-aos-disabled>Never animates</div>
   //
   // CONFIGURATION OPTIONS:
   // ----------------------
   // offset: (number, default: 120)
   //   How many pixels element must be visible before animating
   //   Example: 200 = element animates after scrolling 200px into viewport
   //
   // once: (boolean, default: true)
   //   Whether animation should happen only once
   //   true = animation runs once and stays in final state
   //   false = animation reverses when scrolling back up
   //
   // duration: (number, default: 400)
   //   Default animation duration in milliseconds (override with data-aos-duration)
   //
   // easing: (string, default: 'ease')
   //   Default animation easing function (override with data-aos-easing)
   //
   // disableBelow: (number | function | null, default: null)
   //   Disable animations below viewport width or via custom function
   //   Examples:
   //     768 = disable below 768px width
   //     () => window.innerWidth < 768 = custom function
   //     null = never disable
   //
   // resizeDebounce: (number, default: 250)
   //   Debounce time in milliseconds for window resize events
   //   Prevents excessive checks when resizing window
   //
   // observeDomChanges: (boolean, default: true)
   //   Auto-detect new [data-aos] elements added to DOM
   //   true = MutationObserver watches for new elements (recommended)
   //   false = must manually call refresh() for new content
   //
   // domChangeDebounce: (number, default: 100)
   //   Debounce time in milliseconds for DOM change detection
   //   Prevents excessive refreshes when DOM changes rapidly
   //
   // startEvent: (string, default: 'DOMContentLoaded')
   //   Event to trigger initialization
   //
   // threshold: (number, default: 0)
   //   IntersectionObserver threshold (0-1)
   //   0 = trigger as soon as any pixel is visible
   //
   // SPECIAL ATTRIBUTES:
   // -------------------
   // data-aos-disabled:
   //   Element will NEVER be observed or animated
   //   Use for elements you want to completely exclude
   //
   // data-aos-manual:
   //   Element is observed but won't animate until attribute is removed
   //   Use for lazy-loaded content (product recommendations, variants, etc.)
   //   Remove the attribute to allow animation: element.removeAttribute('data-aos-manual')
   //   MutationObserver will automatically detect the removal and animate
   //
   // METHODS:
   // --------
   // CustomAOS.init(options) - Initialize with custom config
   // CustomAOS.refresh() - Scan for new [data-aos] elements
   // CustomAOS.refreshHard() - Complete reset and re-scan
   // CustomAOS.animate(element) - Manually trigger animation
   // CustomAOS.reset(element) - Reset element animation state
   // CustomAOS.disable() - Disable all animations globally
   // CustomAOS.enable() - Re-enable animations globally
   // CustomAOS.destroy() - Clean up all observers and state
   //
   // CSS CLASSES:
   // ------------
   // .aos-animate - Added when element should animate
   // .aos-finished - Added when animation completes (if once=true)
   // .aos-disabled - Added to <html> when animations are globally disabled
   //
   // ============================================================================

   const CustomAOS = {
      // Default Configuration
      // These can be overridden by passing options to init()
      config: {
         offset: 120, // Default offset in pixels (trigger after 120px visible)
         once: true, // Animate only once by default
         duration: 400, // Animation duration (for reference, actual duration in CSS)
         easing: 'ease', // Animation easing (for reference, actual easing in CSS)
         disableBelow: null, // Disable below viewport width (number in px) or custom function
         resizeDebounce: 250, // Debounce time for window resize (ms)
         observeDomChanges: true, // Auto-detect new [data-aos] elements in DOM
         domChangeDebounce: 100, // Debounce time for DOM changes (ms)
         startEvent: 'DOMContentLoaded', // When to initialize
         threshold: 0, // IntersectionObserver threshold
      },

      // Internal state
      observers: new Map(), // Map of offset -> IntersectionObserver
      elements: new Set(), // Set of all observed elements
      animatingElements: new Map(), // Map of element -> animation state
      isDisabled: false, // Current disabled state
      disableCheckFn: null, // Compiled disable check function
      resizeDebounceTimer: null, // Debounce timer for resize
      domObserver: null, // MutationObserver for DOM changes
      domChangeTimer: null, // Debounce timer for DOM changes

      /**
       * Initialize CustomAOS
       * @param {Object} options - Configuration options
       */
      init(options = {}) {
         // Merge options with defaults
         this.config = { ...this.config, ...options };

         // Compile disable check function
         this.compileDisableCheck();

         // Initial disable check (in case not caught by FOC prevention)
         this.checkAndToggleDisable();

         // Setup resize listener
         this.setupResizeListener();

         // Only initialize observers and DOM observer if not disabled
         if (!this.isDisabled) {
            // Setup DOM change observer
            this.setupDomObserver();

            if (document.readyState === 'loading') {
               document.addEventListener(this.config.startEvent, () => this.refresh());
            } else {
               this.refresh();
            }
         }

         // console.log('[CustomAOS] Initialized with config:', this.config);
      },

      /**
       * Compile disableBelow config into a check function
       */
      compileDisableCheck() {
         const config = this.config.disableBelow;
         // console.log(config);

         if (config === null || config === false || config === undefined) {
            this.disableCheckFn = () => false; // Never disable
         } else if (typeof config === 'number') {
            this.disableCheckFn = () => window.innerWidth < config;
         } else if (typeof config === 'function') {
            this.disableCheckFn = config;
         } else {
            console.warn('[CustomAOS] Invalid disableBelow value. Must be number or function.');
            this.disableCheckFn = () => false;
         }
      },

      /**
       * Check if animations should be disabled and toggle state if changed
       */
      checkAndToggleDisable() {
         if (!this.disableCheckFn) return;

         const shouldDisable = this.disableCheckFn();
         // console.log('shouldDisable', shouldDisable);

         // No change in state
         if (shouldDisable === this.isDisabled) return;

         this.isDisabled = shouldDisable;

         if (shouldDisable) {
            this.disable();
         } else {
            this.enable();
         }

         // console.log(`[CustomAOS] Animations ${shouldDisable ? 'disabled' : 'enabled'}`);
      },

      /**
       * Disable animations globally
       */
      disable() {
         // 1. Destroy all IntersectionObservers
         this.observers.forEach(observer => observer.disconnect());
         this.observers.clear();

         // 2. Disconnect MutationObserver
         if (this.domObserver) {
            this.domObserver.disconnect();
            this.domObserver = null;
         }

         // 3. Add class to <html> tag
         document.documentElement.classList.add('aos-disabled');

         // 4. Remove all .aos-animate classes (reset animations)
         document.querySelectorAll('.aos-animate').forEach(el => {
            el.classList.remove('aos-animate');
         });

         // 5. Clear element tracking
         this.elements.clear();
         this.animatingElements.clear();

         // console.log('[CustomAOS] Disabled - observers destroyed, animations reset');
      },

      /**
       * Enable animations globally
       */
      enable() {
         // 1. Remove class from <html> tag
         document.documentElement.classList.remove('aos-disabled');

         // 2. Restart MutationObserver if it was stopped
         if (this.config.observeDomChanges && !this.domObserver) {
            this.setupDomObserver();
         }

         // 3. Recreate IntersectionObservers and start observing
         this.refresh();

         // console.log('[CustomAOS] Enabled - observers recreated');
      },

      /**
       * Setup window resize listener with debounce
       */
      setupResizeListener() {
         if (!this.disableCheckFn) return;

         window.addEventListener('resize', () => {
            clearTimeout(this.resizeDebounceTimer);

            this.resizeDebounceTimer = setTimeout(() => {
               this.checkAndToggleDisable();
            }, this.config.resizeDebounce);
         });
      },

      /**
       * Setup DOM MutationObserver to auto-detect new [data-aos] elements
       * Watches for:
       * - New nodes added with [data-aos] attribute
       * - Existing nodes getting [data-aos] attribute added
       */
      setupDomObserver() {
         if (!this.config.observeDomChanges) return;
         if (this.isDisabled) return; // Don't start observer if globally disabled

         // Wait for body to be available
         if (!document.body) {
            document.addEventListener('DOMContentLoaded', () => this.setupDomObserver());
            return;
         }

         this.domObserver = new MutationObserver(mutations => {
            let hasNewAosElements = false;

            mutations.forEach(mutation => {
               // Check for added nodes with [data-aos]
               if (mutation.type === 'childList') {
                  mutation.addedNodes.forEach(node => {
                     // Only process element nodes
                     if (node.nodeType === 1) {
                        // Check if node itself has data-aos or contains elements with data-aos
                        if (node.hasAttribute?.('data-aos') || node.querySelector?.('[data-aos]')) {
                           hasNewAosElements = true;
                        }
                     }
                  });
               }

               // Check for attribute changes (data-aos added to existing element)
               if (mutation.type === 'attributes' && mutation.attributeName === 'data-aos') {
                  hasNewAosElements = true;
               }
            });

            // Trigger debounced refresh if new AOS elements detected
            if (hasNewAosElements) {
               this.debouncedRefresh();
            }
         });

         // Start observing the entire document body
         this.domObserver.observe(document.body, {
            childList: true, // Watch for added/removed nodes
            subtree: true, // Watch entire tree (all descendants)
            attributes: true, // Watch attribute changes
            attributeFilter: ['data-aos'], // Only watch data-aos attribute
         });

         // console.log('[CustomAOS] DOM observer started');
      },

      /**
       * Debounced refresh to prevent excessive calls when DOM changes rapidly
       */
      debouncedRefresh() {
         clearTimeout(this.domChangeTimer);

         this.domChangeTimer = setTimeout(() => {
            // console.log('[CustomAOS] DOM changed - refreshing...');
            this.refresh();
         }, this.config.domChangeDebounce);
      },

      /**
       * Refresh: Scan for new [data-aos] elements and observe them
       * Use this when lazy-loading content
       */
      refresh() {
         // Don't create observers if globally disabled
         if (this.isDisabled) {
            // console.log('[CustomAOS] Refresh skipped - animations are disabled');
            return;
         }

         // console.log('[CustomAOS] Refreshing...');

         // Find all elements with data-aos attribute
         const elements = document.querySelectorAll('[data-aos]');

         elements.forEach(el => {
            // Skip if already observing this element
            if (this.elements.has(el)) return;

            // Skip if completely disabled (cannot be animated at all)
            if (el.hasAttribute('data-aos-disabled')) return;

            // Note: We DO observe elements with data-aos-manual
            // They will be skipped in animateElement() until attribute is removed

            // Add to tracking
            this.elements.add(el);

            // Get element's offset (default to global config)
            const offset = parseInt(el.getAttribute('data-aos-offset')) || this.config.offset;

            // Get or create observer for this offset
            const observer = this.getOrCreateObserver(offset);

            // Start observing
            observer.observe(el);
         });

         // console.log(`[CustomAOS] Now observing ${this.elements.size} elements`);
      },

      /**
       * RefreshHard: Complete reset and re-initialization
       * Disconnects all observers, clears state, and re-scans everything
       */
      refreshHard() {
         // console.log('[CustomAOS] Hard refresh - resetting everything...');

         // Disconnect all observers
         this.observers.forEach(observer => observer.disconnect());

         // Clear all state
         this.observers.clear();
         this.elements.clear();
         this.animatingElements.clear();

         // Remove all aos-animate classes
         document.querySelectorAll('.aos-animate').forEach(el => {
            el.classList.remove('aos-animate');
         });

         // Re-check disable state
         this.checkAndToggleDisable();

         // Re-scan and observe (if not disabled)
         if (!this.isDisabled) {
            this.refresh();
         }
      },

      /**
       * Get or create an IntersectionObserver for a specific offset
       * @param {number} offset - The offset in pixels
       * @returns {IntersectionObserver}
       */
      getOrCreateObserver(offset) {
         // Check if we already have an observer for this offset
         if (this.observers.has(offset)) {
            return this.observers.get(offset);
         }

         // Create rootMargin: negative offset means element must be X pixels visible
         // E.g., offset=200 → rootMargin="-200px" → element must scroll 200px into viewport
         const rootMargin = offset > 0 ? `-${offset}px 0px -${offset}px 0px` : '0px';

         // Create new observer
         const observer = new IntersectionObserver(entries => this.handleIntersection(entries), {
            rootMargin: rootMargin,
            threshold: this.config.threshold,
         });

         // Store observer
         this.observers.set(offset, observer);

         // console.log(`[CustomAOS] Created observer for offset: ${offset}px (rootMargin: ${rootMargin})`);

         return observer;
      },

      /**
       * Handle intersection events
       * @param {IntersectionObserverEntry[]} entries
       */
      handleIntersection(entries) {
         entries.forEach(entry => {
            const element = entry.target;

            // Element is entering viewport
            if (entry.isIntersecting) {
               this.animateElement(element);
            }
            // Element is leaving viewport (only matters if not "once")
            else {
               const once = element.hasAttribute('data-aos-once') ? element.getAttribute('data-aos-once') === 'true' : this.config.once;

               // If not "once", remove animation when leaving viewport
               if (!once && !element.classList.contains('aos-finished')) {
                  element.classList.remove('aos-animate');
               }
            }
         });
      },

      /**
       * Animate an element (add .aos-animate class)
       * @param {HTMLElement} element
       */
      animateElement(element) {
         // Skip if completely disabled (cannot be animated at all)
         if (element.hasAttribute('data-aos-disabled')) return;

         // Skip if manual (not being observed, requires manual initialization)
         if (element.hasAttribute('data-aos-manual')) return;

         // Skip if already finished (for "once" animations)
         if (element.classList.contains('aos-finished')) return;

         // Add animate class
         element.classList.add('aos-animate');

         // console.log('[CustomAOS] Animating:', element);

         // Check if this is a "once" animation
         const once = element.hasAttribute('data-aos-once') ? element.getAttribute('data-aos-once') === 'true' : this.config.once;

         // If "once", listen for animation completion
         if (once) {
            this.listenForAnimationComplete(element);
         }
      },

      /**
       * Listen for animation/transition completion
       * @param {HTMLElement} element
       */
      listenForAnimationComplete(element) {
         // Avoid adding multiple listeners
         if (this.animatingElements.has(element)) return;

         const handleComplete = () => {
            // Mark as finished
            element.classList.add('aos-finished');

            // console.log('[CustomAOS] Animation complete:', element);

            // Get the observer for this element's offset
            const offset = parseInt(element.getAttribute('data-aos-offset')) || this.config.offset;
            const observer = this.observers.get(offset);

            // Stop observing this element
            if (observer) {
               observer.unobserve(element);
            }

            // Remove from tracking
            this.animatingElements.delete(element);

            // Clean up listeners
            element.removeEventListener('transitionend', handleComplete);
            element.removeEventListener('animationend', handleComplete);
         };

         // Listen for both transition and animation end events
         element.addEventListener('transitionend', handleComplete, { once: true });
         element.addEventListener('animationend', handleComplete, { once: true });

         // Track that we're listening
         this.animatingElements.set(element, true);
      },

      /**
       * Manually trigger animation on an element
       * Useful for elements with data-aos-disabled that you want to animate programmatically
       * @param {HTMLElement|string} elementOrSelector - Element or selector
       */
      animate(elementOrSelector) {
         const element = typeof elementOrSelector === 'string' ? document.querySelector(elementOrSelector) : elementOrSelector;

         if (!element) {
            console.warn('[CustomAOS] Element not found:', elementOrSelector);
            return;
         }

         // Force animate even if disabled
         this.animateElement(element);
      },

      /**
       * Reset an element's animation (remove .aos-animate class)
       * @param {HTMLElement|string} elementOrSelector - Element or selector
       */
      reset(elementOrSelector) {
         const element = typeof elementOrSelector === 'string' ? document.querySelector(elementOrSelector) : elementOrSelector;

         if (!element) {
            console.warn('[CustomAOS] Element not found:', elementOrSelector);
            return;
         }

         element.classList.remove('aos-animate', 'aos-finished');
         // console.log('[CustomAOS] Reset:', element);
      },

      /**
       * Destroy CustomAOS and clean up all observers and timers
       * Useful for cleanup when unmounting or reinitializing
       */
      destroy() {
         // Disconnect all IntersectionObservers
         this.observers.forEach(observer => observer.disconnect());
         this.observers.clear();

         // Disconnect MutationObserver
         if (this.domObserver) {
            this.domObserver.disconnect();
            this.domObserver = null;
         }

         // Clear all timers
         clearTimeout(this.domChangeTimer);
         clearTimeout(this.resizeDebounceTimer);

         // Clear all tracking
         this.elements.clear();
         this.animatingElements.clear();

         // Remove class from <html> tag
         document.documentElement.classList.remove('aos-disabled');

         // console.log('[CustomAOS] Destroyed - all observers disconnected');
      },
   };
   window.CustomAOS = CustomAOS;
})();
